import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';

const Reports = () => {
  const [activeTab, setActiveTab] =
    useState('deliveries');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const reportEndpoints = {
    deliveries: 'deliveries',
    engineers: 'engineers',
    inventory: 'inventory',
    sla: 'sla',
    customers: 'customers',
  };

  const reportFilenames = {
    deliveries: 'delivery_report.pdf',
    engineers: 'engineer_report.pdf',
    inventory: 'inventory_report.pdf',
    sla: 'sla_report.pdf',
    customers: 'customer_report.pdf',
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await reportsAPI.exportPDF(
        reportEndpoints[activeTab]);
      const url = window.URL.createObjectURL(
        new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download',
        reportFilenames[activeTab]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadReport(activeTab);
  }, [activeTab]);

  const loadReport = async (tab) => {
    setLoading(true);
    setData(null);
    try {
      let res;
      if (tab === 'deliveries')
        res = await reportsAPI.getDeliveries();
      else if (tab === 'engineers')
        res = await reportsAPI.getEngineers();
      else if (tab === 'inventory')
        res = await reportsAPI.getInventory();
      else if (tab === 'sla')
        res = await reportsAPI.getSLA();
      else if (tab === 'customers')
        res = await reportsAPI.getCustomers();
      setData(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { key:'deliveries', label:'Delivery Report' },
    { key:'engineers',
      label:'Engineer Performance' },
    { key:'inventory',
      label:'Inventory Report' },
    { key:'sla', label:'SLA Report' },
    { key:'customers',
      label:'Customer Report' },
  ];

  const StatBox = ({ label, value, color }) => (
    <div style={{
      background:'var(--surface2)',
      borderRadius:'8px', padding:'14px',
      borderTop:`2px solid ${color}`,
    }}>
      <div style={{
        fontSize:'10px',
        fontFamily:'DM Mono,monospace',
        color:'var(--text3)',
        letterSpacing:'1px',
        textTransform:'uppercase',
        marginBottom:'6px',
      }}>
        {label}
      </div>
      <div style={{
        fontFamily:'Syne,sans-serif',
        fontSize:'26px', fontWeight:'800',
        color: color,
      }}>
        {value ?? 0}
      </div>
    </div>
  );

  const thStyle = {
    padding:'10px 14px', textAlign:'left',
    fontSize:'10px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1.5px',
    textTransform:'uppercase',
    borderBottom:'1px solid var(--border)',
    background:'var(--surface2)',
    whiteSpace:'nowrap',
  };

  const tdStyle = {
    padding:'11px 14px', fontSize:'13px',
    borderBottom:'1px solid var(--border)',
  };

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'flex-start',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Reports & Analytics
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Business insights and performance data
          </p>
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exporting || !data}
          style={{
            background:'var(--accent)',
            color:'var(--bg)', border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px', fontWeight:'600',
            cursor: (exporting || !data)
              ? 'not-allowed' : 'pointer',
            opacity: (exporting || !data) ? 0.5 : 1,
          }}
        >
          {exporting ? 'Exporting...' : '⬇ Export PDF'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display:'flex', gap:'0',
        borderBottom:'1px solid var(--border)',
        marginBottom:'24px',
        overflowX:'auto',
      }}>
        {tabs.map(t => (
          <div key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding:'10px 20px',
              cursor:'pointer',
              fontSize:'13px', fontWeight:'500',
              color: activeTab === t.key
                ? 'var(--accent)'
                : 'var(--text3)',
              borderBottom:
                activeTab === t.key
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
              marginBottom:'-1px',
              transition:'all 0.15s',
              whiteSpace:'nowrap',
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{
          padding:'60px', textAlign:'center',
          color:'var(--text3)',
          fontFamily:'DM Mono,monospace',
        }}>
          Loading report...
        </div>
      ) : !data ? (
        <div style={{
          padding:'60px', textAlign:'center',
          color:'var(--text3)',
        }}>
          No data available
        </div>
      ) : (

        <>
          {/* DELIVERY REPORT */}
          {activeTab === 'deliveries' &&
            data.summary && (
            <>
              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(5,1fr)',
                gap:'12px', marginBottom:'20px',
              }}>
                <StatBox
                  label="Total Tickets"
                  value={data.summary.total_tickets}
                  color="var(--accent)" />
                <StatBox
                  label="Completed"
                  value={data.summary.completed}
                  color="var(--green)" />
                <StatBox
                  label="In Progress"
                  value={data.summary.in_progress}
                  color="var(--yellow)" />
                <StatBox
                  label="Cancelled"
                  value={data.summary.cancelled}
                  color="var(--red)" />
                <StatBox
                  label="Avg TAT (hrs)"
                  value={
                    data.summary.avg_tat_hours
                    || '—'}
                  color="var(--purple)" />
              </div>
              <div style={{
                background:'var(--surface)',
                border:'1px solid var(--border)',
                borderRadius:'12px',
                overflow:'hidden',
              }}>
                <div style={{
                  padding:'14px 20px',
                  borderBottom:
                    '1px solid var(--border)',
                  fontFamily:'Syne,sans-serif',
                  fontSize:'14px',
                  fontWeight:'700',
                }}>
                  Delivery Log
                </div>
                {data.tickets?.length === 0 ? (
                  <div style={{
                    padding:'40px',
                    textAlign:'center',
                    color:'var(--text3)',
                    fontSize:'13px',
                  }}>
                    No tickets yet
                  </div>
                ) : (
                  <table style={{
                    width:'100%',
                    borderCollapse:'collapse',
                  }}>
                    <thead>
                      <tr>
                        {['Ticket','Type',
                          'Customer','Engineer',
                          'Status','Scheduled',
                          'Completed','Proof'
                        ].map(h => (
                          <th key={h}
                            style={thStyle}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.tickets?.map(t => (
                        <tr key={t.ticket_number}>
                          <td style={{
                            ...tdStyle,
                            fontFamily:
                              'DM Mono,monospace',
                            color:'var(--accent)',
                            fontWeight:'600',
                          }}>
                            {t.ticket_number}
                          </td>
                          <td style={{
                            ...tdStyle,
                            color:'var(--text2)',
                            textTransform:
                              'capitalize',
                          }}>
                            {t.ticket_type
                              ?.replace('_',' ')}
                          </td>
                          <td style={tdStyle}>
                            {t.customer || '—'}
                          </td>
                          <td style={{
                            ...tdStyle,
                            color:'var(--text2)',
                          }}>
                            {t.engineer || '—'}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              background:
                                t.status ===
                                'closed'
                                  ? 'rgba(77,95,122,0.2)'
                                : t.status ===
                                  'delivered'
                                  ? 'rgba(0,229,160,0.1)'
                                : 'rgba(255,192,64,0.1)',
                              color:
                                t.status ===
                                'closed'
                                  ? 'var(--text3)'
                                : t.status ===
                                  'delivered'
                                  ? 'var(--green)'
                                : 'var(--yellow)',
                              padding:'3px 10px',
                              borderRadius:'20px',
                              fontSize:'11px',
                              fontFamily:
                                'DM Mono,monospace',
                              textTransform:
                                'capitalize',
                            }}>
                              {t.status}
                            </span>
                          </td>
                          <td style={{
                            ...tdStyle,
                            fontFamily:
                              'DM Mono,monospace',
                            color:'var(--text3)',
                            fontSize:'12px',
                          }}>
                            {t.scheduled_date
                              ? new Date(
                                  t.scheduled_date
                                ).toLocaleDateString(
                                  'en-IN')
                              : '—'}
                          </td>
                          <td style={{
                            ...tdStyle,
                            fontFamily:
                              'DM Mono,monospace',
                            color:'var(--text3)',
                            fontSize:'12px',
                          }}>
                            {t.completed_at
                              ? new Date(
                                  t.completed_at
                                ).toLocaleDateString(
                                  'en-IN')
                              : '—'}
                          </td>
                          <td style={tdStyle}>
                            {t.customer_acknowledged
                              ? <span style={{
                                  color:
                                    'var(--green)',
                                  fontSize:'11px',
                                }}>
                                  ✓ Confirmed
                                </span>
                              : <span style={{
                                  color:
                                    'var(--text3)',
                                  fontSize:'11px',
                                }}>
                                  Pending
                                </span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* ENGINEER REPORT */}
          {activeTab === 'engineers' && (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              overflow:'hidden',
            }}>
              <div style={{
                padding:'14px 20px',
                borderBottom:
                  '1px solid var(--border)',
                fontFamily:'Syne,sans-serif',
                fontSize:'14px', fontWeight:'700',
              }}>
                Engineer Performance
              </div>
              {!data || !Array.isArray(data) || data.length === 0 ? (
                <div style={{
                  padding:'40px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'13px',
                }}>
                  No engineer data yet
                </div>
              ) : (
                <table style={{
                  width:'100%',
                  borderCollapse:'collapse',
                }}>
                  <thead>
                    <tr>
                      {['Engineer','ID',
                        'Assigned','Completed',
                        'In Progress',
                        'Avg TAT (hrs)',
                        'On-Time %','Status'
                      ].map(h => (
                        <th key={h}
                          style={thStyle}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(e => (
                      <tr key={e.id}>
                        <td style={{
                          ...tdStyle,
                          fontWeight:'600',
                        }}>
                          {e.name}
                        </td>
                        <td style={{
                          ...tdStyle,
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--text3)',
                          fontSize:'11px',
                        }}>
                          {e.employee_id}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {e.total_assigned}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:'var(--green)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {e.completed}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:'var(--yellow)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {e.in_progress}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--text2)',
                        }}>
                          {e.avg_tat_hours
                            || '—'}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                        }}>
                          <span style={{
                            color:
                              parseFloat(
                                e.on_time_percentage
                              ) >= 90
                                ? 'var(--green)'
                              : parseFloat(
                                  e.on_time_percentage
                                ) >= 70
                                ? 'var(--yellow)'
                              : 'var(--red)',
                            fontFamily:
                              'DM Mono,monospace',
                            fontWeight:'600',
                          }}>
                            {e.on_time_percentage
                              ? e.on_time_percentage
                                + '%'
                              : '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            fontSize:'11px',
                            padding:'2px 8px',
                            borderRadius:'10px',
                            background:
                              e.current_status
                              === 'active'
                                ? 'rgba(0,229,160,0.1)'
                              : 'rgba(77,95,122,0.2)',
                            color:
                              e.current_status
                              === 'active'
                                ? 'var(--green)'
                              : 'var(--text3)',
                          }}>
                            {e.current_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* INVENTORY REPORT */}
          {activeTab === 'inventory' &&
            data.summary && (
            <>
              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(4,1fr)',
                gap:'12px', marginBottom:'20px',
              }}>
                <StatBox
                  label="Total Items"
                  value={data.summary.total_items}
                  color="var(--accent)" />
                <StatBox
                  label="Available"
                  value={data.summary.available}
                  color="var(--green)" />
                <StatBox
                  label="Assigned"
                  value={data.summary.assigned}
                  color="var(--yellow)" />
                <StatBox
                  label="Out of Stock"
                  value={data.summary.out_of_stock}
                  color="var(--red)" />
              </div>
              <div style={{
                background:'var(--surface)',
                border:'1px solid var(--border)',
                borderRadius:'12px',
                overflow:'hidden',
              }}>
                <div style={{
                  padding:'14px 20px',
                  borderBottom:
                    '1px solid var(--border)',
                  fontFamily:'Syne,sans-serif',
                  fontSize:'14px',
                  fontWeight:'700',
                }}>
                  Inventory Movement
                </div>
                <table style={{
                  width:'100%',
                  borderCollapse:'collapse',
                }}>
                  <thead>
                    <tr>
                      {['Product','Category',
                        'Grade','Type',
                        'Available','Assigned',
                        'Dispatched','Sale Price'
                      ].map(h => (
                        <th key={h}
                          style={thStyle}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.movement?.map(
                      (item, i) => (
                      <tr key={i}>
                        <td style={{
                          ...tdStyle,
                          fontWeight:'600',
                        }}>
                          {item.product_name}
                        </td>
                        <td style={{
                          ...tdStyle,
                          color:'var(--text2)',
                          fontSize:'12px',
                        }}>
                          {item.category || '—'}
                        </td>
                        <td style={{
                          ...tdStyle,
                          color:
                            item.grade === 'A'
                            || item.grade === 'A+'
                              ? 'var(--green)'
                            : 'var(--yellow)',
                          fontFamily:
                            'DM Mono,monospace',
                          fontWeight:'600',
                        }}>
                          {item.grade}
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background:
                              item.order_type
                              === 'sale'
                                ? 'rgba(0,212,255,0.1)'
                              : 'rgba(159,122,234,0.1)',
                            color:
                              item.order_type
                              === 'sale'
                                ? 'var(--accent)'
                              : 'var(--purple)',
                            padding:'2px 8px',
                            borderRadius:'10px',
                            fontSize:'11px',
                            fontFamily:
                              'DM Mono,monospace',
                            textTransform:
                              'capitalize',
                          }}>
                            {item.order_type}
                          </span>
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:
                            item.quantity_available
                            > 0
                              ? 'var(--green)'
                            : 'var(--red)',
                          fontFamily:
                            'DM Mono,monospace',
                          fontWeight:'600',
                        }}>
                          {item.quantity_available}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:'var(--yellow)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {item.quantity_assigned}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--text2)',
                        }}>
                          {item.times_dispatched}
                        </td>
                        <td style={{
                          ...tdStyle,
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--text2)',
                        }}>
                          {item.sale_price
                            ? `₹${Number(
                                item.sale_price
                              ).toLocaleString(
                                'en-IN')}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* SLA REPORT */}
          {activeTab === 'sla' &&
            data.summary && (
            <>
              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(3,1fr)',
                gap:'12px', marginBottom:'20px',
              }}>
                <StatBox
                  label="Total Tickets"
                  value={data.summary.total}
                  color="var(--accent)" />
                <StatBox
                  label="Within SLA"
                  value={data.summary.within_sla}
                  color="var(--green)" />
                <StatBox
                  label="SLA Breaches"
                  value={data.summary.breached}
                  color="var(--red)" />
              </div>
              <div style={{
                background:'var(--surface)',
                border:'1px solid var(--border)',
                borderRadius:'12px',
                overflow:'hidden',
              }}>
                <div style={{
                  padding:'14px 20px',
                  borderBottom:
                    '1px solid var(--border)',
                  fontFamily:'Syne,sans-serif',
                  fontSize:'14px',
                  fontWeight:'700',
                }}>
                  SLA Breach Log
                </div>
                {data.breaches?.length === 0 ? (
                  <div style={{
                    padding:'40px',
                    textAlign:'center',
                    color:'var(--green)',
                    fontSize:'14px',
                  }}>
                    ✓ No SLA breaches!
                    All tickets within SLA.
                  </div>
                ) : (
                  <table style={{
                    width:'100%',
                    borderCollapse:'collapse',
                  }}>
                    <thead>
                      <tr>
                        {['Ticket','Type',
                          'Customer','Engineer',
                          'SLA Deadline',
                          'Hours Over SLA',
                          'Status'
                        ].map(h => (
                          <th key={h}
                            style={thStyle}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.breaches?.map(b => (
                        <tr
                          key={b.ticket_number}>
                          <td style={{
                            ...tdStyle,
                            fontFamily:
                              'DM Mono,monospace',
                            color:'var(--red)',
                            fontWeight:'600',
                          }}>
                            {b.ticket_number}
                          </td>
                          <td style={{
                            ...tdStyle,
                            textTransform:
                              'capitalize',
                          }}>
                            {b.ticket_type
                              ?.replace('_',' ')}
                          </td>
                          <td style={tdStyle}>
                            {b.customer || '—'}
                          </td>
                          <td style={{
                            ...tdStyle,
                            color:'var(--text2)',
                          }}>
                            {b.engineer || '—'}
                          </td>
                          <td style={{
                            ...tdStyle,
                            fontFamily:
                              'DM Mono,monospace',
                            color:'var(--text3)',
                            fontSize:'12px',
                          }}>
                            {new Date(
                              b.sla_deadline
                            ).toLocaleString(
                              'en-IN')}
                          </td>
                          <td style={{
                            ...tdStyle,
                            textAlign:'center',
                            color:'var(--red)',
                            fontFamily:
                              'DM Mono,monospace',
                            fontWeight:'600',
                          }}>
                            +{b.hours_over_sla}h
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              fontSize:'11px',
                              padding:'2px 8px',
                              borderRadius:'10px',
                              background:
                                'rgba(255,77,109,0.1)',
                              color:'var(--red)',
                            }}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* CUSTOMER REPORT */}
          {activeTab === 'customers' && (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              overflow:'hidden',
            }}>
              <div style={{
                padding:'14px 20px',
                borderBottom:
                  '1px solid var(--border)',
                fontFamily:'Syne,sans-serif',
                fontSize:'14px', fontWeight:'700',
              }}>
                Customer Summary
              </div>
              {!data || data.length === 0 ? (
                <div style={{
                  padding:'40px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'13px',
                }}>
                  No customer data yet
                </div>
              ) : (
                <table style={{
                  width:'100%',
                  borderCollapse:'collapse',
                }}>
                  <thead>
                    <tr>
                      {['Company','City',
                        'Total Orders',
                        'Sale Orders',
                        'Rental Orders',
                        'Completed','Active',
                        'Last Order'
                      ].map(h => (
                        <th key={h}
                          style={thStyle}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data) && data.map((c, i) => (
                      <tr key={i}>
                        <td style={{
                          ...tdStyle,
                          fontWeight:'600',
                        }}>
                          {c.company_name}
                        </td>
                        <td style={{
                          ...tdStyle,
                          color:'var(--text2)',
                        }}>
                          {c.city || '—'}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--accent)',
                          fontWeight:'600',
                        }}>
                          {c.total_orders}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--accent)',
                        }}>
                          {c.sale_orders}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--purple)',
                        }}>
                          {c.rental_orders}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:'var(--green)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {c.completed}
                        </td>
                        <td style={{
                          ...tdStyle,
                          textAlign:'center',
                          color:'var(--yellow)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {c.active}
                        </td>
                        <td style={{
                          ...tdStyle,
                          fontFamily:
                            'DM Mono,monospace',
                          color:'var(--text3)',
                          fontSize:'12px',
                        }}>
                          {c.last_order_date
                            ? new Date(
                                c.last_order_date
                              ).toLocaleDateString(
                                'en-IN')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;